import { useMemo } from 'react';

import { Align, Column, Columns, Row, Rows, Title, TitleLevels, TitleMarkup } from '@bytel/trilogy-react';

import { ConfigType } from '@app-types/api';
import { FaiProduct } from '@app-types/fai';
import { JOURNEYS, PlanType, RepeaterProduct } from '@app-types/product';

import { ChangePlanCategory } from '@components/home/change-plan-category';
import HomeDivider from '@components/layout/home-divider';

import { isPack } from '@helpers/product';

import { BAndyouConfigurator, BboxConfigurator } from '@landing/components';

function shouldDisplayInJourney(journey: JOURNEYS, plan: PlanType) {
    return isPack(plan) ? journey === JOURNEYS.PACKS : journey !== JOURNEYS.PACKS;
}

type HomePageConfiguratorsProps = {
    journey: JOURNEYS;
    plans: PlanType[];
    faiProducts: FaiProduct[];
    isMobileFirst: boolean;
    repeaters?: { [key: string]: RepeaterProduct };
    config?: ConfigType;
    isLoading: boolean;
};

function MobileFirstLayout({
    journey,
    plans: _plans,
    faiProducts,
    repeaters,
    isLoading,
    config,
}: Readonly<Omit<HomePageConfiguratorsProps, 'isMobileFirst'>>) {
    const { mainPlans, otherPlans } = useMemo(() => {
        return _plans.reduce(
            (acc: { mainPlans: PlanType[]; otherPlans: PlanType[] }, item) => {
                if (shouldDisplayInJourney(journey, item)) {
                    acc.mainPlans.push(item);
                } else {
                    acc.otherPlans.push(item);
                }
                return acc;
            },
            { mainPlans: [], otherPlans: [] },
        );
    }, [journey, _plans]);

    const cheapestOtherPLans = useMemo(() => {
        return otherPlans.reduce((acc: PlanType | undefined, item) => {
            if (item.prices.final < (acc?.prices?.final ?? Infinity)) {
                return item;
            }

            return acc;
        }, otherPlans[0]);
    }, [otherPlans]);

    return (
        <Columns align={Align.CENTER}>
            <Column size={7}>
                <Title level={TitleLevels.THREE} markup={TitleMarkup.H1}>
                    {journey === JOURNEYS.PACKS
                        ? 'Nos packs divertissement sans\u00A0engagement'
                        : 'Nos forfaits mobile sans\u00A0engagement B&YOU'}
                </Title>
                <Rows gap={8}>
                    <Row narrow>
                        <BAndyouConfigurator
                            journey={journey}
                            plans={mainPlans}
                            isMobileFirst={true}
                            isLoading={isLoading}
                            config={config}
                        />
                    </Row>
                    {cheapestOtherPLans && <ChangePlanCategory journey={journey} plan={cheapestOtherPLans} />}
                    <HomeDivider />
                    <Row narrow>
                        <Title level={TitleLevels.THREE} markup={TitleMarkup.H2}>
                            Et si vous ajoutiez une offre fibre sans engagement&nbsp;?
                        </Title>
                        <BboxConfigurator
                            faiProducts={faiProducts}
                            repeaters={repeaters}
                            isLoading={isLoading}
                            config={config}
                        />
                    </Row>
                </Rows>
            </Column>
        </Columns>
    );
}

function FiberFirstLayout({
    journey,
    plans: _plans,
    faiProducts,
    repeaters,
    isLoading,
    config,
}: Readonly<Omit<HomePageConfiguratorsProps, 'isMobileFirst' | 'featureFaiBlockActivated'>>) {
    const plans = useMemo(() => _plans.filter((plan) => shouldDisplayInJourney(journey, plan)), [journey, _plans]);

    return (
        <Columns align={Align.CENTER}>
            <Column size={7}>
                <Title level={TitleLevels.THREE} markup={TitleMarkup.H1}>
                    Nos box internet sans engagement
                </Title>
                <Rows gap={8}>
                    <Row narrow>
                        <BboxConfigurator
                            faiProducts={faiProducts}
                            repeaters={repeaters}
                            isLoading={isLoading}
                            config={config}
                        />
                    </Row>
                    <HomeDivider />
                    <Row narrow>
                        <Title level={TitleLevels.THREE} markup={TitleMarkup.H2}>
                            Et si vous ajoutiez un forfait sans engagement&nbsp;?
                        </Title>
                        <BAndyouConfigurator
                            journey={journey}
                            plans={plans}
                            isMobileFirst={false}
                            isLoading={isLoading}
                            config={config}
                        />
                    </Row>
                </Rows>
            </Column>
        </Columns>
    );
}

export default function HomePageConfigurators({
    journey,
    plans,
    faiProducts,
    isMobileFirst,
    repeaters,
    isLoading,
    config,
}: Readonly<HomePageConfiguratorsProps>) {
    if (isMobileFirst) {
        return (
            <MobileFirstLayout
                journey={journey}
                plans={plans}
                faiProducts={faiProducts}
                repeaters={repeaters}
                isLoading={isLoading}
                config={config}
            />
        );
    }

    return (
        <FiberFirstLayout
            journey={journey}
            plans={plans}
            faiProducts={faiProducts}
            repeaters={repeaters}
            isLoading={isLoading}
            config={config}
        />
    );
}
